/**
 * HustleMeetMatchmakerIntakePage.jsx — /matchmaker/intake
 *
 * The single questionnaire that feeds Circles + Matchmaker. Fetches
 * the questionnaire spec + interest bank from the backend, PATCHes
 * /api/hustlemeet-profile/mine as the member fills it in. Save-as-
 * you-go — no wizard step lost if the user drops off.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Save, CheckCircle2, ArrowRight, Heart } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const AVAIL = ["weekday_eve", "weekend_lunch", "weekend_eve", "sat_late", "sun_arvo"];
const AVAIL_LABEL = {
  weekday_eve:   "Weekday evenings",
  weekend_lunch: "Weekend lunches",
  weekend_eve:   "Weekend evenings",
  sat_late:      "Saturday late",
  sun_arvo:      "Sunday afternoons",
};

export default function HustleMeetMatchmakerIntakePage() {
  const [profile, setProfile]     = useState(null);
  const [interests, setInterests] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [savedAt, setSavedAt]     = useState(null);
  const [err, setErr]             = useState("");
  const [preview, setPreview]     = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: mine }, { data: bank }] = await Promise.all([
          axios.get(`${API}/hustlemeet-profile/mine`),
          axios.get(`${API}/hustlemeet-profile/interests`),
        ]);
        setProfile(mine || {});
        setInterests(bank?.interests || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Sign in to complete your Matchmaker intake.");
      }
    })();
  }, []);

  // Live match preview — recompute 300 ms after the profile stops changing.
  useEffect(() => {
    if (!profile) return;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const body = {
          big5:          profile.big5      || {},
          values:        profile.values    || {},
          interests:     profile.interests || [],
          dietary:       profile.dietary   || {},
          logistics:     profile.logistics || {},
        };
        const { data } = await axios.post(
          `${API}/hustlemeet-profile/mine/preview-matches`, body);
        setPreview(data?.matches || []);
      } catch { /* ignore preview errors */ }
      finally { setPreviewLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [profile]);

  function patch(fields) {
    setProfile((p) => ({ ...(p || {}), ...fields }));
  }
  function patchNested(section, fields) {
    setProfile((p) => ({ ...(p || {}), [section]: { ...(p?.[section] || {}), ...fields } }));
  }
  function toggleInterest(name) {
    const cur = (profile?.interests || []);
    if (cur.includes(name)) {
      patch({ interests: cur.filter((i) => i !== name) });
    } else if (cur.length < 5) {
      patch({ interests: [...cur, name] });
    }
  }
  function toggleAvail(slot) {
    const cur = profile?.logistics?.availability || [];
    patchNested("logistics", {
      availability: cur.includes(slot) ? cur.filter((x) => x !== slot) : [...cur, slot],
    });
  }

  async function save() {
    setSaving(true); setErr("");
    try {
      const body = {
        dating_optin:           !!profile.dating_optin,
        community_standards_ok: !!profile.community_standards_ok,
        big5:         profile.big5      || {},
        values:       profile.values    || {},
        interests:    profile.interests || [],
        life_stage:   profile.life_stage || {},
        dietary:      profile.dietary   || {},
        logistics:    profile.logistics || {},
        partner_prefs: profile.partner_prefs || {},
      };
      const { data } = await axios.patch(`${API}/hustlemeet-profile/mine`, body);
      setProfile(data);
      setSavedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  if (err && !profile) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-lg font-black">{err}</p>
          <Link to="/login" data-testid="link-login"
                className="inline-flex items-center gap-1 mt-6 text-amber-500 font-bold hover:underline">
            Sign in <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400" data-testid="intake-loading">
        Loading…
      </div>
    );
  }

  const pct = profile.completion_pct ?? 0;
  const ready = !!profile.dating_ready;

  return (
    <div data-testid="matchmaker-intake-page" className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100">
      <PlatformHeader
        brand={{ name: "Matchmaker Intake", to: "/matchmaker" }}
        showBookCta={false} showCart={false}
      />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        <header>
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">HustleMeet · Matchmaker</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-black leading-tight">Your Matchmaker profile</h1>
          <p className="mt-2 text-stone-600 dark:text-stone-300 text-sm">
            One questionnaire feeds both Circles and Matchmaker. Save-as-you-go — nothing lost if you close the tab.
          </p>
          <div className="mt-5 rounded-full bg-stone-100 dark:bg-stone-900 h-2 overflow-hidden" data-testid="completion-bar">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-stone-500" data-testid="completion-pct">
            {pct}% complete{ready ? " · Matchmaker-ready ✓" : ""}
          </p>
        </header>

        {/* Live match preview — three archetype scores that move as members fill in */}
        <section data-testid="intake-preview-matches"
                 className="rounded-2xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/5 p-6">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-lg font-black text-stone-900 dark:text-stone-100">
              How you'd match right now
            </h2>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400" data-testid="preview-live-tag">
              {previewLoading ? "Scoring…" : "Live"}
            </span>
          </div>
          <p className="mt-1 text-xs text-stone-600 dark:text-stone-300">
            Three sample members from the Circle roster — watch the scores climb as you fill in more of your profile.
          </p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {(preview.length ? preview : [{name:"—",photo:null,score_pct:0},{name:"—",photo:null,score_pct:0},{name:"—",photo:null,score_pct:0}]).map((m, i) => (
              <div key={i} data-testid={`preview-match-${i}`}
                   className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-3 flex items-center gap-3">
                {m.photo
                  ? <img src={m.photo} alt="" className="w-11 h-11 rounded-full object-cover shrink-0 ring-1 ring-amber-500/30" />
                  : <span className="w-11 h-11 rounded-full bg-stone-100 dark:bg-stone-800 grid place-items-center shrink-0 text-stone-400">•</span>}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-black text-stone-900 dark:text-stone-100 truncate">{m.name}</p>
                  <div className="mt-1 rounded-full bg-stone-100 dark:bg-stone-900 h-1.5 overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all duration-500"
                         style={{ width: `${m.score_pct}%` }} />
                  </div>
                  <p className="mt-1 text-[10px] font-bold tabular-nums text-amber-600 dark:text-amber-400"
                     data-testid={`preview-match-score-${i}`}>
                    {m.score_pct}% compatible
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Consent */}
        <section data-testid="intake-consent" className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 space-y-3">
          <h2 className="text-lg font-black">Consent</h2>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="chk-dating-optin"
                   checked={!!profile.dating_optin}
                   onChange={(e) => patch({ dating_optin: e.target.checked })}
                   className="mt-1" />
            <span>I opt in to the Matchmaker — I want to receive intro emails for compatible members at Circle dinners.</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="chk-community-ok"
                   checked={!!profile.community_standards_ok}
                   onChange={(e) => patch({ community_standards_ok: e.target.checked })}
                   className="mt-1" />
            <span>I accept the HustleMeet Community Standards — kindness, respect, honesty.</span>
          </label>
        </section>

        {/* Interests */}
        <section data-testid="intake-interests" className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <h2 className="text-lg font-black">Interests <span className="text-xs font-normal text-stone-500 ml-2">Pick up to 5</span></h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {interests.map((i) => {
              const on = (profile.interests || []).includes(i);
              return (
                <button key={i} data-testid={`interest-${i.replace(/\s+/g,"-").toLowerCase()}`}
                        type="button" onClick={() => toggleInterest(i)}
                        className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${on
                          ? "bg-amber-500 text-slate-950 border-amber-500"
                          : "border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-amber-400"}`}>
                  {i}
                </button>
              );
            })}
          </div>
        </section>

        {/* Personality (Big 5, 1-7) */}
        <section data-testid="intake-big5" className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <h2 className="text-lg font-black">Personality snapshot</h2>
          <p className="mt-1 text-xs text-stone-500">Rate each 1 (not at all) to 7 (very much).</p>
          <div className="mt-4 grid gap-4">
            {[
              ["O", "Openness — curious, imaginative"],
              ["C", "Conscientiousness — organised, reliable"],
              ["E", "Extraversion — outgoing, energetic"],
              ["A", "Agreeableness — warm, cooperative"],
              ["N", "Emotional sensitivity"],
            ].map(([k, label]) => (
              <label key={k} className="text-sm flex items-center gap-4">
                <span className="w-52 shrink-0">{label}</span>
                <input type="range" min={1} max={7} step={1}
                       data-testid={`big5-${k}`}
                       value={profile.big5?.[k] ?? 4}
                       onChange={(e) => patchNested("big5", { [k]: parseInt(e.target.value, 10) })}
                       className="flex-1 accent-amber-500" />
                <span className="w-6 text-right font-black">{profile.big5?.[k] ?? 4}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Dietary */}
        <section data-testid="intake-dietary" className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <h2 className="text-lg font-black">Dietary</h2>
          <p className="mt-1 text-xs text-stone-500">Sent to the venue 7 days before every Circle dinner.</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {["is_vegetarian","is_vegan","is_pescatarian","halal","kosher","gluten_free","dairy_free","alcohol_free"].map((k) => (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" data-testid={`diet-${k}`}
                       checked={!!profile.dietary?.[k]}
                       onChange={(e) => patchNested("dietary", { [k]: e.target.checked })} />
                {k.replace(/^is_/,"").replace(/_/g," ")}
              </label>
            ))}
          </div>
          <label className="block mt-4 text-sm">
            Allergies (comma-separated)
            <input type="text" data-testid="diet-allergies"
                   value={(profile.dietary?.allergies || []).join(", ")}
                   onChange={(e) => patchNested("dietary", {
                     allergies: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                   })}
                   className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2 text-sm" />
          </label>
          <label className="block mt-3 text-sm">
            Notes for the kitchen
            <textarea data-testid="diet-notes" rows={2}
                      value={profile.dietary?.notes || ""}
                      onChange={(e) => patchNested("dietary", { notes: e.target.value })}
                      className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2 text-sm" />
          </label>
        </section>

        {/* Logistics */}
        <section data-testid="intake-logistics" className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <h2 className="text-lg font-black">Logistics</h2>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            <label className="text-sm">Home suburb
              <input type="text" data-testid="log-suburb"
                     value={profile.logistics?.home_suburb || ""}
                     onChange={(e) => patchNested("logistics", { home_suburb: e.target.value })}
                     className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2" />
            </label>
            <label className="text-sm">State
              <input type="text" data-testid="log-state"
                     value={profile.logistics?.home_state || ""}
                     onChange={(e) => patchNested("logistics", { home_state: e.target.value })}
                     className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2" />
            </label>
            <label className="text-sm">Max travel km
              <input type="number" min={0} max={500} data-testid="log-maxkm"
                     value={profile.logistics?.max_travel_km ?? 30}
                     onChange={(e) => patchNested("logistics", { max_travel_km: parseInt(e.target.value, 10) || 0 })}
                     className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2" />
            </label>
          </div>
          <p className="mt-4 text-xs font-bold text-stone-500 uppercase tracking-widest">Availability</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {AVAIL.map((slot) => {
              const on = (profile.logistics?.availability || []).includes(slot);
              return (
                <button key={slot} data-testid={`avail-${slot}`}
                        type="button" onClick={() => toggleAvail(slot)}
                        className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${on
                          ? "bg-amber-500 text-slate-950 border-amber-500"
                          : "border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-amber-400"}`}>
                  {AVAIL_LABEL[slot]}
                </button>
              );
            })}
          </div>
        </section>

        {/* Partner preferences — dating only */}
        {profile.dating_optin && (
          <section data-testid="intake-partner-prefs" className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
            <div className="flex items-center gap-2">
              <Heart size={16} className="text-amber-500" />
              <h2 className="text-lg font-black">Partner preferences <span className="text-xs font-normal text-stone-500 ml-2">Dating only</span></h2>
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
              <label>Age min
                <input type="number" min={18} max={99}
                       data-testid="pref-age-min"
                       value={profile.partner_prefs?.age_min ?? 25}
                       onChange={(e) => patchNested("partner_prefs", { age_min: parseInt(e.target.value, 10) || 18 })}
                       className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2" />
              </label>
              <label>Age max
                <input type="number" min={18} max={99}
                       data-testid="pref-age-max"
                       value={profile.partner_prefs?.age_max ?? 45}
                       onChange={(e) => patchNested("partner_prefs", { age_max: parseInt(e.target.value, 10) || 99 })}
                       className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2" />
              </label>
              <label>Gender preference
                <select data-testid="pref-gender"
                        value={profile.partner_prefs?.gender_pref || "any"}
                        onChange={(e) => patchNested("partner_prefs", { gender_pref: e.target.value })}
                        className="mt-1 w-full rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2">
                  <option value="any">Any</option>
                  <option value="woman">Women</option>
                  <option value="man">Men</option>
                  <option value="non_binary">Non-binary</option>
                </select>
              </label>
            </div>
          </section>
        )}

        {/* Save */}
        <div className="flex flex-wrap items-center gap-3 sticky bottom-4 z-10 backdrop-blur-sm bg-slate-50/80 dark:bg-[#0a0908]/80 border border-stone-200 dark:border-stone-800 rounded-full p-3 pl-5 shadow-lg">
          {err && <span className="text-red-500 text-sm mr-2" data-testid="intake-error">{err}</span>}
          {savedAt && !err && (
            <span className="inline-flex items-center gap-1 text-emerald-500 text-sm font-bold" data-testid="intake-saved">
              <CheckCircle2 size={14} /> Saved · {savedAt.toLocaleTimeString()}
            </span>
          )}
          <button data-testid="btn-save-intake" onClick={save} disabled={saving}
                  className="ml-auto rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-5 py-2.5 text-sm inline-flex items-center gap-1 disabled:opacity-60">
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </main>
      <SuiteFooter />
    </div>
  );
}
